import React from 'react';

import { array, not, pipe, remote, task } from '@code-expert/prelude';
import type { UserId } from '/imports/domain';
import { invokeQuery } from '/imports/ui/hooks/useProcedure';

export interface UserNameInterface {
  _id: UserId;
  userName: string;
  email: string;
  userTitleName: string;
}

/**
 * In-memory cache of username data
 */
export const mkUserNameStore = (
  fetch: (ids: Array<UserId>) => task.Task<Array<UserNameInterface>>,
) => {
  let store: Record<UserId, remote.Remote<UserNameInterface>> = {};

  const withLazyMutation = (): [
    (id: UserId, val: remote.Remote<UserNameInterface>) => void,
    () => void,
  ] => {
    const storeBefore = store;
    const notify = () => {
      if (store !== storeBefore) listeners.forEach((x) => x());
    };
    let set = (id: UserId, val: remote.Remote<UserNameInterface>): void => {
      store = { ...store };
      set = (id, val) => void (store[id] = val);
      set(id, val);
    };
    return [(id, val) => set(id, val), notify];
  };

  const listeners = new Set<VoidFunction>();
  const subscribe = (listener: VoidFunction): VoidFunction => {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  };

  const exists = (id: UserId) => id in store;
  const initUserNames = (ids: Array<UserId>) => {
    const [set, notify] = withLazyMutation();
    for (const id of ids) if (!exists(id)) set(id, remote.pending);
    notify();
  };
  const setUserNames = (users: Array<UserNameInterface>) => {
    const [set, notify] = withLazyMutation();
    for (const user of users) set(user._id, remote.done(user));
    notify();
  };
  const fillCache = (userIds: Array<UserId>) => {
    void (async () => {
      const newIds = pipe(userIds, array.filter(not(exists)));
      if (array.isNonEmpty(newIds)) {
        initUserNames(newIds);
        const users = await task.run(fetch(newIds));
        setUserNames(users);
      }
    })();
  };

  return {
    fillCache,
    subscribe,
    getSnapshot: () => store,
  };
};

const userNameStore = mkUserNameStore(invokeQuery('user_getNames'));

/**
 * Hook to improve performance of getting the userName by using an in-memory cache
 */
export const useUserNameCache = () => {
  const cache = React.useSyncExternalStore(userNameStore.subscribe, userNameStore.getSnapshot);

  const makeLookup = React.useCallback(
    (userIds: Array<UserId>) => {
      userNameStore.fillCache(userIds);
      return (id: UserId) => cache[id] ?? remote.initial;
    },
    [cache],
  );

  /**
   * Find all usernames for a list of user IDs
   */
  const getUsersFromCache = React.useCallback(
    (userIds: Array<UserId>) => {
      const lookup = makeLookup(userIds);
      return pipe(userIds, array.traverse(remote.Applicative)(lookup));
    },
    [makeLookup],
  );

  /**
   * Find the username for a specific user ID
   */
  const getUserFromCache = React.useCallback(
    (userId: UserId) => {
      const lookup = makeLookup([userId]);
      return lookup(userId);
    },
    [makeLookup],
  );

  return React.useMemo(
    () => ({ getUserFromCache, getUsersFromCache, fillCache: userNameStore.fillCache }),
    [getUserFromCache, getUsersFromCache],
  );
};
